/**
 * Hierarchical Clustering Analysis (HCA)
 * Agglomerative clustering with various linkage methods
 */

import { mean } from '../core/math.js';
import { prepareX } from '../core/table.js';

/**
 * Compute Euclidean distance between two points
 * @param {Array<number>} a - First point
 * @param {Array<number>} b - Second point
 * @returns {number} Distance
 */
function euclideanDistance(a, b) {
  let sum = 0;
  for (let i = 0; i < a.length; i++) {
    sum += (a[i] - b[i]) ** 2;
  }
  return Math.sqrt(sum);
}

/**
 * Compute pairwise distance matrix
 * @param {Array<Array<number>>} data - Data points
 * @returns {Array<Array<number>>} Distance matrix
 */
function computeDistanceMatrix(data) {
  const n = data.length;
  const distances = Array(n).fill(null).map(() => Array(n).fill(0));

  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const dist = euclideanDistance(data[i], data[j]);
      distances[i][j] = dist;
      distances[j][i] = dist;
    }
  }

  return distances;
}

/**
 * Compute mean vector for a cluster
 * @param {Array<number>} cluster - Indices in cluster
 * @param {Array<Array<number>>} data - Data matrix
 * @returns {Array<number>} Mean vector
 */
function clusterMean(cluster, data) {
  const dimension = data[0].length;
  const meanVector = Array(dimension).fill(0);

  for (const index of cluster) {
    const point = data[index];
    for (let i = 0; i < dimension; i++) {
      meanVector[i] += point[i];
    }
  }

  const size = cluster.length;
  for (let i = 0; i < dimension; i++) {
    meanVector[i] /= size;
  }

  return meanVector;
}

/**
 * Compute Ward linkage distance between two clusters
 * @param {Array<number>} cluster1 - Indices in cluster 1
 * @param {Array<number>} cluster2 - Indices in cluster 2
 * @param {Array<Array<number>>} data - Data matrix
 * @returns {number} Ward distance
 */
function wardDistance(cluster1, cluster2, data) {
  const size1 = cluster1.length;
  const size2 = cluster2.length;
  const mean1 = clusterMean(cluster1, data);
  const mean2 = clusterMean(cluster2, data);

  let squaredDiff = 0;
  for (let i = 0; i < mean1.length; i++) {
    const diff = mean1[i] - mean2[i];
    squaredDiff += diff * diff;
  }

  return (size1 * size2) / (size1 + size2) * squaredDiff;
}

/**
 * Find minimum distance between two clusters
 * @param {Array<number>} cluster1 - Indices in cluster 1
 * @param {Array<number>} cluster2 - Indices in cluster 2
 * @param {Array<Array<number>>} distances - Distance matrix
 * @param {string} linkage - Linkage method
 * @param {Array<Array<number>>} data - Data matrix (required for Ward)
 * @returns {number} Distance between clusters
 */
function clusterDistance(cluster1, cluster2, distances, linkage, data) {
  if (linkage === 'ward') {
    if (!data) {
      throw new Error('Ward linkage requires access to the original data matrix.');
    }
    return wardDistance(cluster1, cluster2, data);
  }

  const dists = [];

  for (const i of cluster1) {
    for (const j of cluster2) {
      dists.push(distances[i][j]);
    }
  }

  if (linkage === 'single') {
    return Math.min(...dists);
  } else if (linkage === 'complete') {
    return Math.max(...dists);
  } else if (linkage === 'average') {
    return mean(dists);
  }

  throw new Error(`Unknown linkage: ${linkage}`);
}

/**
 * Fit hierarchical clustering
 * @param {Array<Array<number>>} X - Data matrix
 * @param {Object} options - {linkage: 'single'|'complete'|'average'|'ward'}
 * @returns {Object} {dendrogram, distances}
 */
export function fit(X, { linkage = 'average' } = {}) {
  let data;

  if (
    X && typeof X === 'object' && !Array.isArray(X) && (X.data || X.columns)
  ) {
    const opts = X;
    const data_in = opts.data;
    const columns = opts.columns;
    const omit_missing = opts.omit_missing !== undefined ? opts.omit_missing : true;

    if (!data_in) {
      throw new Error(
        'Declarative call requires a `data` property (array-of-objects or Arquero-like table)',
      );
    }

    const prepared = prepareX({ columns, data: data_in, omit_missing });
    data = prepared.X;

    linkage = opts.linkage !== undefined ? opts.linkage : linkage;
  } else if (Array.isArray(X) && X.length > 0 && typeof X[0] === 'object' && !Array.isArray(X[0])) {
    // Array of objects (table-like data) - use prepareX
    const prepared = prepareX({ columns: null, data: X, omit_missing: true });
    data = prepared.X;
  } else if (Array.isArray(X)) {
    // Array of arrays (numeric matrix)
    data = X.map((row) => Array.isArray(row) ? row : [row]);
  } else {
    throw new Error(
      'X must be an array of rows or an options object containing `data`/`columns`',
    );
  }

  const n = data.length;

  if (n < 2) {
    throw new Error('Need at least 2 samples for clustering');
  }

  const distances = computeDistanceMatrix(data);

  const clusters = data.map((_, i) => [i]);
  const merges = [];

  while (clusters.length > 1) {
    let minDist = Infinity;
    let minI = 0;
    let minJ = 1;

    for (let i = 0; i < clusters.length; i++) {
      for (let j = i + 1; j < clusters.length; j++) {
        const dist = clusterDistance(
          clusters[i],
          clusters[j],
          distances,
          linkage,
          data,
        );
        if (dist < minDist) {
          minDist = dist;
          minI = i;
          minJ = j;
        }
      }
    }

    const cluster1Indices = clusters[minI].slice().sort((a, b) => a - b);
    const cluster2Indices = clusters[minJ].slice().sort((a, b) => a - b);
    const merged = [...cluster1Indices, ...cluster2Indices].sort((a, b) => a - b);

    merges.push({
      cluster1: cluster1Indices,
      cluster2: cluster2Indices,
      distance: minDist,
      size: merged.length,
    });

    const newClusters = [];
    for (let i = 0; i < clusters.length; i++) {
      if (i !== minI && i !== minJ) {
        newClusters.push(clusters[i]);
      }
    }
    newClusters.push(merged);
    clusters.splice(0, clusters.length, ...newClusters);
  }

  return {
    dendrogram: merges,
    linkage,
    n,
  };
}

export function cut(model, k) {
  const { dendrogram, n } = model;

  if (k < 1 || k > n) {
    throw new Error(`k must be between 1 and ${n}`);
  }

  if (k === n) {
    return Array.from({ length: n }, (_, i) => i);
  }

  const clusterMap = new Map();
  for (let i = 0; i < n; i++) {
    clusterMap.set(String(i), [i]);
  }

  const joinKey = (arr) => arr.join('|');

  for (let i = 0; i < dendrogram.length && clusterMap.size > k; i++) {
    const merge = dendrogram[i];
    const key1 = joinKey(merge.cluster1);
    const key2 = joinKey(merge.cluster2);

    const cluster1 = clusterMap.get(key1);
    const cluster2 = clusterMap.get(key2);

    if (!cluster1 || !cluster2) {
      continue;
    }

    clusterMap.delete(key1);
    clusterMap.delete(key2);

    const merged = [...cluster1, ...cluster2].sort((a, b) => a - b);
    clusterMap.set(joinKey(merged), merged);
  }

  const clusters = [...clusterMap.values()];
  const labels = Array(n).fill(-1);
  clusters.forEach((cluster, idx) => {
    cluster.forEach((item) => {
      labels[item] = idx;
    });
  });

  return labels;
}

export function cutHeight(model, height) {
  const { dendrogram, n } = model;

  if (height < 0) {
    throw new Error('height must be non-negative');
  }

  const clusterMap = new Map();
  for (let i = 0; i < n; i++) {
    clusterMap.set(String(i), [i]);
  }

  const joinKey = (arr) => arr.join('|');

  for (let i = 0; i < dendrogram.length; i++) {
    const merge = dendrogram[i];
    const { cluster1, cluster2, distance } = merge;

    if (distance > height) {
      continue;
    }

    const key1 = joinKey(cluster1);
    const key2 = joinKey(cluster2);

    const cluster1Arr = clusterMap.get(key1);
    const cluster2Arr = clusterMap.get(key2);

    if (!cluster1Arr || !cluster2Arr) {
      continue;
    }

    clusterMap.delete(key1);
    clusterMap.delete(key2);

    const merged = [...cluster1Arr, ...cluster2Arr].sort((a, b) => a - b);
    clusterMap.set(joinKey(merged), merged);
  }

  const clusters = [...clusterMap.values()];
  const labels = Array(n).fill(-1);
  clusters.forEach((cluster, idx) => {
    cluster.forEach((item) => {
      labels[item] = idx;
    });
  });

  return labels;
}

export default {
  fit,
  cut,
  cutHeight,
};
